import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api/client.ts';
import { Game } from './components/Game.tsx';
import { Home } from './components/Home.tsx';
import { SettingsDialog } from './components/Dialogs.tsx';
import { Stats } from './components/Stats.tsx';
import { HowToPlay } from './help/HowToPlay.tsx';
import { newGame, type GameState } from './game/state.ts';
import { LEVELS, type Level } from './game/types.ts';
import { fromSaved, storage, type SavedGame, type Settings } from './storage/storage.ts';
import { useTheme } from './theme.ts';

type Screen = { name: 'home' } | { name: 'stats' } | { name: 'game'; game: GameState; key: string };

const loadSaved = () => {
  const out: Partial<Record<Level, SavedGame>> = {};
  for (const level of LEVELS) {
    const g = storage.loadGame(level);
    if (g) out[level] = g;
  }
  return out;
};

export function App() {
  const [settings, setSettings] = useState<Settings>(() => storage.loadSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  // How to play opens by itself on the first visit, once.
  const [helpOpen, setHelpOpen] = useState(() => !storage.introSeen());
  useEffect(() => {
    if (helpOpen) storage.markIntroSeen();
  }, [helpOpen]);
  const [screen, setScreen] = useState<Screen>({ name: 'home' });
  const [saved, setSaved] = useState(loadSaved);
  const [loading, setLoading] = useState<Level | null>(null);
  const [error, setError] = useState<string | null>(null);

  useTheme(settings);

  const updateSettings = (s: Settings) => {
    setSettings(s);
    storage.saveSettings(s);
  };

  const play = useCallback(async (level: Level, fresh: boolean) => {
    setError(null);
    const existing = fresh ? null : storage.loadGame(level);
    if (existing) {
      setScreen({ name: 'game', game: fromSaved(existing), key: `${existing.puzzle.id}-${Date.now()}` });
      return;
    }
    setLoading(level);
    try {
      const puzzle = await api.nextPuzzle(level, storage.recentPuzzles(level));
      storage.rememberPuzzle(level, puzzle.id);
      const game = newGame(puzzle);
      storage.saveGame(game);
      // A game counts as started once its board is shown.
      storage.recordStart({ level, puzzleId: puzzle.id, startedAt: game.startedAt });
      setScreen({ name: 'game', game, key: `${puzzle.id}-${Date.now()}` });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Could not load a puzzle. Try again.';
      setError(message);
      setScreen({ name: 'home' });
      setSaved(loadSaved());
    } finally {
      setLoading(null);
    }
  }, []);

  const goHome = () => {
    setScreen({ name: 'home' });
    // The game saves itself as it closes; read after that has happened.
    setTimeout(() => setSaved(loadSaved()), 0);
  };

  return (
    <>
      {screen.name === 'stats' ? (
        <Stats onBack={() => setScreen({ name: 'home' })} />
      ) : screen.name === 'home' ? (
        <Home
          saved={saved}
          loading={loading}
          error={error}
          onPlay={play}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenStats={() => setScreen({ name: 'stats' })}
          onOpenHelp={() => setHelpOpen(true)}
        />
      ) : (
        <Game
          key={screen.key}
          initial={screen.game}
          settings={settings}
          modalOpen={settingsOpen}
          helpOpen={helpOpen}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenHelp={() => setHelpOpen(true)}
          onSettingsChange={updateSettings}
          onHome={goHome}
          onNewGame={() => play(screen.game.puzzle.level, true)}
        />
      )}
      {helpOpen ? <HowToPlay onClose={() => setHelpOpen(false)} /> : null}
      <SettingsDialog
        open={settingsOpen}
        settings={settings}
        onChange={updateSettings}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}
